/**
 * Chat Client
 * HTTP client for communicating with trd-agent backend
 */

import Axios from "axios";
import loadStore from "@/utils/generateStore";
import type { Conversation, ChatMessage } from "@/types/chat.types";

/**
 * Create a separate axios instance for trd-agent
 * This allows us to have different baseURL and configuration
 */
const createChatAxios = async () => {
  const store = await loadStore();
  const token = await store.get("token");

  const instance = Axios.create({
    // trd-agent detrás del PEP del BFF (#125): SÓLO al BFF; el borde afirma el
    // contexto (businessId/userId) del token, el front no lo manda.
    baseURL: `${import.meta.env.VITE_BFF_URL}/api/v1/agent`,
    headers: {
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    },
  });

  return instance;
};

/**
 * Chat Client class
 * Handles all communication with trd-agent backend
 */
class ChatClient {
  /**
   * Get all conversations for current user
   */
  async getConversations(): Promise<Conversation[]> {
    const axios = await createChatAxios();
    const response = await axios.get<{
      conversations: Conversation[];
      pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
      };
    }>("/conversations");
    return response.data.conversations;
  }

  /**
   * Get a specific conversation with messages
   */
  async getConversation(
    id: string
  ): Promise<{ conversation: Conversation; messages: ChatMessage[] }> {
    const axios = await createChatAxios();
    const response = await axios.get<{
      conversation: Conversation;
      messages: ChatMessage[];
    }>(`/conversations/${id}`);
    return response.data;
  }

  /**
   * Delete a conversation
   */
  async deleteConversation(id: string): Promise<void> {
    const axios = await createChatAxios();
    await axios.delete(`/conversations/${id}`);
  }

  /**
   * Update conversation metadata (e.g., title)
   */
  async updateConversation(
    id: string,
    updates: Partial<Conversation>
  ): Promise<Conversation> {
    const axios = await createChatAxios();
    const response = await axios.patch<Conversation>(
      `/conversations/${id}`,
      updates
    );
    return response.data;
  }
}

// Export singleton instance
export const chatClient = new ChatClient();
